/**
 * Reports Page - Auto-generated reports from pipeline Gold phase
 *
 * v3.77 - 2026-03-13 - SHOW-01: Added ShowYourWork panel below each report card
 * v3.22 - 2026-02-18 - Rewrite: auto-generated reports as primary content, hide manual wizard
 * v2.97 - 2026-02-07 - Report builder: dynamic reports from Gold table data
 *
 * Shows auto-generated reports sorted by relevance (discovery-aligned first).
 * Manual ReportBuilder kept in codebase but hidden per zero-lift philosophy.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  TableChart as TableChartIcon,
  PieChart as PieChartIcon,
  ShowChart as LineChartIcon,
  Close as CloseIcon,
  Description as ReportIcon,
} from '@mui/icons-material';
import TopBar from '../components/TopBar';
import ReportViewer from '../components/ReportViewer';
import ShowYourWork from '../components/ShowYourWork';
import { reportService, Report } from '../services/api';

// v3.22 - Chart type icon mapping
const chartIcons: Record<string, React.ReactElement> = {
  bar: <BarChartIcon />,
  table: <TableChartIcon />,
  pie: <PieChartIcon />,
  line: <LineChartIcon />,
};

// v3.22 - Category color mapping (same pattern as Dashboards.tsx)
const categoryColorMap: Record<string, string> = {
  financial: '#4CAF50',
  operational: '#FF9800',
  sales: '#2196F3',
  overview: '#9C27B0',
  default: '#607D8B',
};

const getCategoryColor = (category?: string): string => {
  if (!category) return categoryColorMap.default;
  return categoryColorMap[category.toLowerCase()] || categoryColorMap.default;
};

// v3.22 - Loading skeleton
const ReportsSkeleton: React.FC = () => (
  <Grid container spacing={3}>
    {[1, 2, 3, 4, 5, 6].map((i) => (
      <Grid item xs={12} sm={6} md={4} key={i}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="80%" height={24} />
                <Skeleton variant="text" width="40%" />
              </Box>
            </Box>
            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="60%" sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" width="100%" height={32} />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

// v3.22 - Empty state
const EmptyState: React.FC = () => (
  <Box
    sx={{
      textAlign: 'center',
      py: 8,
      px: 3,
      bgcolor: 'background.paper',
      borderRadius: 2,
      border: '1px dashed',
      borderColor: 'divider',
    }}
  >
    <ReportIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
    <Typography variant="h6" gutterBottom>
      No Reports Available
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 400, mx: 'auto' }}>
      Reports will be generated after your data pipeline completes.
      Connect a data source and run your pipeline to get started.
    </Typography>
  </Box>
);

const Reports: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewReport, setViewReport] = useState<Report | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // v3.22 - Fetch and sort reports
  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await reportService.getAll();
      const allReports = result.reports || [];
      // v3.22 - Show auto-generated reports + legacy reports without origin field
      const visibleReports = allReports.filter(
        (r) => r.origin === 'auto' || !r.origin
      );
      // Sort by relevanceScore DESC then createdAt DESC
      visibleReports.sort((a, b) => {
        const scoreA = a.relevanceScore ?? 0;
        const scoreB = b.relevanceScore ?? 0;
        if (scoreB !== scoreA) return scoreB - scoreA;
        return (b.createdAt || '').localeCompare(a.createdAt || '');
      });
      setReports(visibleReports);
    } catch (err: any) {
      console.error('Failed to fetch reports:', err);
      setError(err.userMessage || err.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // v3.22 - Open report viewer dialog
  const handleOpenReport = (report: Report) => {
    setViewReport(report);
    setDialogOpen(true);
  };

  return (
    // v2.98 - Mobile responsive padding - 2026-03-20
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <TopBar title="Reports" subtitle="Auto-generated analysis and trend reports" />
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <ReportsSkeleton />
        ) : reports.length === 0 ? (
          <EmptyState />
        ) : (
          <Grid container spacing={3}>
            {reports.map((report) => {
              const color = getCategoryColor(report.category);
              const isAuto = report.origin === 'auto';
              return (
                <Grid item xs={12} sm={6} md={4} key={report.id}>
                  <Card
                    sx={{
                      height: '100%',
                      borderLeft: `4px solid ${color}`,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
                    }}
                  >
                    {/* v3.69 - VIZ-01: Thumbnail preview (og:image 1200×628 rendered by chart-renderer) */}
                    {report.previewImageUrl && (
                      <img
                        src={report.previewImageUrl}
                        alt={report.title}
                        onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
                      />
                    )}
                    <CardActionArea
                      onClick={() => handleOpenReport(report)}
                      sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                    >
                      <CardContent sx={{ flex: 1 }}>
                        {/* Header with chart icon */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              bgcolor: `${color}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: color,
                            }}
                          >
                            {chartIcons[report.chartType] || <TableChartIcon />}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                              {report.title}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {report.category && (
                                <Chip
                                  label={report.category}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    bgcolor: `${color}15`,
                                    color: color,
                                    fontWeight: 500,
                                  }}
                                />
                              )}
                              <Chip
                                label={report.chartType}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            </Box>
                          </Box>
                        </Box>

                        {/* Description */}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2, minHeight: 40, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                        >
                          {report.description || 'Business analysis report'}
                        </Typography>

                        {/* Footer */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            {isAuto ? 'Auto-generated' : `Created ${new Date(report.createdAt).toLocaleDateString()}`}
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                    {/* v3.77 - SHOW-01: "How we found this" expandable trace panel */}
                    <ShowYourWork entityType="insight" entityId={report.id} />
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>

      {/* v3.22 - Report viewer dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '60vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {viewReport?.title || 'Report'}
          </Typography>
          <IconButton onClick={() => setDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {viewReport && (
            <ReportViewer report={viewReport} onClose={() => setDialogOpen(false)} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Reports;
